import { useState } from 'react';
import { LOCATION_TYPE } from '@locales/id/labels';
import { LOCATION_FILTERS as T, LOCATION_STATUS } from '@locales/id/locations';
import { LOCATION_STATUSES, LOCATION_TYPES, type LocationStatus, type LocationType, type Province, type Regency } from '@lib/api';
import { Icon, MenuCheck, MenuChoice } from '@ui';
import { AreaPanel } from './AreaPanel';

/** "Kosongkan pilihan" under a checklist, once something is checked. */
function ClearChoice({ shown, onClear }: { shown: boolean; onClear: () => void }) {
  if (!shown) return null;
  return (
    <>
      <hr className="fmenu-sep" />
      <button type="button" className="fmenu-option fmenu-clear" data-menu-item onClick={onClear}>
        {T.clearChoice}
      </button>
    </>
  );
}

/** Jenis: any number of types (none = all), in the order of the distribution chain. */
export function TypePanel({ value, onToggle, onClear }: { value: LocationType[]; onToggle: (t: LocationType) => void; onClear: () => void }) {
  return (
    <div className="fmenu-list" role="group" aria-label={T.type}>
      {LOCATION_TYPES.map((t) => (
        <MenuCheck key={t} checked={value.includes(t)} onToggle={() => onToggle(t)}>
          {LOCATION_TYPE[t]}
        </MenuCheck>
      ))}
      <ClearChoice shown={value.length > 0} onClear={onClear} />
    </div>
  );
}

/** Status: what needs a look (several may be chosen: a location matching any of them is shown). */
export function StatusPanel({ value, onToggle, onClear }: { value: LocationStatus[]; onToggle: (s: LocationStatus) => void; onClear: () => void }) {
  return (
    <div className="fmenu-list" role="group" aria-label={T.status}>
      {LOCATION_STATUSES.map((s) => (
        <MenuCheck key={s} checked={value.includes(s)} onToggle={() => onToggle(s)}>
          <span className="fmenu-option-text">
            <span className="fmenu-option-title">{LOCATION_STATUS[s].label}</span>
            <span className="fmenu-option-sub">{LOCATION_STATUS[s].sub}</span>
          </span>
        </MenuCheck>
      ))}
      <ClearChoice shown={value.length > 0} onClear={onClear} />
    </div>
  );
}

/** A searchable radio list (38 provinces, up to a few dozen regencies). */
function SearchList<T extends { code: string; name: string }>({ heading, all, items, value, search, none, failed, onPick }: { heading: string; all: string; items: T[] | null; value: string; search: string; none: string; failed?: string | null; onPick: (code: string) => void }) {
  const [q, setQ] = useState('');
  const typed = q.trim().toLowerCase();
  const shown = (items ?? []).filter((x) => !typed || x.name.toLowerCase().includes(typed));
  return (
    <>
      <p className="fmenu-heading">{heading}</p>
      <label className="fmenu-search">
        <Icon name="search" size={16} />
        <span className="sr-only">{search}</span>
        <input type="search" value={q} placeholder={search} onChange={(e) => setQ(e.target.value)} data-menu-item autoComplete="off" />
      </label>
      <div className="fmenu-list fmenu-scroll loc-region-list" role="radiogroup" aria-label={heading}>
        {!typed && <MenuChoice checked={!value} onSelect={() => onPick('')}>{all}</MenuChoice>}
        {shown.map((x) => (
          <MenuChoice key={x.code} checked={value === x.code} focusable={!!typed && x === shown[0]} onSelect={() => onPick(x.code)}>
            {x.name}
          </MenuChoice>
        ))}
      </div>
      {failed ? (
        <p className="fmenu-state" role="status">{failed}</p>
      ) : (
        items && !shown.length && <p className="fmenu-state" role="status">{none}</p>
      )}
    </>
  );
}

/**
 * Wilayah: the province (a national account; a province account is locked to its own), then, once a province is
 * set, its regencies/cities. Choosing a province keeps the menu open for the regency. With the area props (a list that
 * filters by kecamatan and desa/kelurahan) the regency keeps it open for its kecamatan and the village closes it;
 * without them the regency closes it (the caller's `onRegency`).
 */
export function RegionPanel({ provinces, regencies, regenciesFailed, provinceCode, regencyCode, locked, onProvince, onRegency, districtCode, villageCode, onDistrict, onVillage }: {
  provinces: Province[] | null;
  regencies: Regency[] | null;
  regenciesFailed: boolean;
  provinceCode: string;
  regencyCode: string;
  locked: boolean;
  onProvince: (code: string) => void;
  onRegency: (code: string) => void;
  districtCode?: string;
  villageCode?: string;
  onDistrict?: (code: string) => void;
  onVillage?: (code: string) => void;
}) {
  return (
    <>
      {!locked && <SearchList heading={T.province} all={T.allProvinces} items={provinces} value={provinceCode} search={T.provinceSearch} none={T.noProvince} onPick={onProvince} />}
      {provinceCode && (
        <>
          {!locked && <hr className="fmenu-sep" />}
          <SearchList key={provinceCode} heading={T.regency} all={T.allRegencies} items={regencies} value={regencyCode} search={T.regencySearch} none={T.noRegency} failed={regenciesFailed ? T.regencyFailed : null} onPick={onRegency} />
        </>
      )}
      {onDistrict && onVillage && <AreaPanel regencyCode={regencyCode} districtCode={districtCode ?? ''} villageCode={villageCode ?? ''} onDistrict={onDistrict} onVillage={onVillage} />}
    </>
  );
}
